import { reflect } from '@effector/reflect';
import { Anchor, NumberFormatter } from '@mantine/core';
import { Link } from 'atomic-router-react';
import { StoreValue } from 'effector';
import { useUnit } from 'effector-react';
import { FileSearch } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { accountTreeModel } from 'entities/account';
import { TIME_ZONES } from 'shared/config';
import { translated } from 'shared/lib';
import { accountRoute, cashdeskRoute, gameSettingsRoute } from 'shared/routing';
import {
	createDateFilter,
	createRegistry,
	MultiSelect,
	RegistryRootProps,
	RegistryTableTfootProps,
} from 'shared/ui';
import { BetReportRegistryModel } from '../../model';
import cls from './bet-report-registry.module.scss';

export type BetReportRegistryProps = RegistryRootProps & {
	emptyActionSlot?: ReactNode;
	topBarActionSlot?: ReactNode;
};

export const createBetReportRegistry = ({
	registryModel,
	dateFilterModel,
	...model
}: BetReportRegistryModel) => {
	const DateFilter = createDateFilter(dateFilterModel);

	const DateField = reflect({
		view: DateFilter.DateField,
		bind: {
			'data-test-id': 'bet-report-registry-date-input',
			className: cls.betReportRegistryDate,
		},
	});

	const TimeZoneField = reflect({
		view: DateFilter.TimeZoneField,
		bind: {
			'data-test-id': 'bet-report-registry-time-zone-filter',
			className: cls.betReportRegistryTimeZone,
			value: registryModel.filters.timeZone.$value.map((tz) => tz.toString()),
			onChange: (value) =>
				registryModel.filters.timeZone.setValue(Number(value ?? TIME_ZONES['UTC-00:00'])),
		},
	});

	const CurrenciesField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'bet-report-registry-currencies-filter',
			value: registryModel.filters.currencies.$value,
			onChange: registryModel.filters.currencies.setValue,
			className: cls.betReportRegistryMultiSelect,
			data: accountTreeModel.$currencies,
			label: translated('bet-report-registry.filters.currencies.label'),
			placeholder: translated('bet-report-registry.filters.currencies.placeholder'),
			nothingFoundMessage: translated('bet-report-registry.filters.currencies.empty'),
			searchable: true,
		},
	});

	const GamesField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'bet-report-registry-games-filter',
			value: registryModel.filters.gameIds.$value.map((ids) => ids.map(String)),
			onChange: (value) => registryModel.filters.gameIds.setValue(value.map(Number)),
			className: cls.betReportRegistryMultiSelect,
			data: model.$games,
			label: translated('bet-report-registry.filters.games.label'),
			placeholder: translated('bet-report-registry.filters.games.placeholder'),
			nothingFoundMessage: translated('bet-report-registry.filters.games.empty'),
		},
	});

	const CashdesksField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'bet-report-registry-cashdesks-filter',
			value: registryModel.filters.cashdeskIds.$value.map((ids) => ids.map(String)),
			onChange: (value) => registryModel.filters.cashdeskIds.setValue(value.map(Number)),
			className: cls.betReportRegistryMultiSelect,
			data: model.$cashdesks,
			label: translated('bet-report-registry.filters.cashdesks.label'),
			placeholder: translated('bet-report-registry.filters.cashdesks.placeholder'),
			nothingFoundMessage: translated('bet-report-registry.filters.cashdesks.empty'),
		},
	});

	const AccountLink = reflect({
		view: Anchor<typeof Link>,
		bind: {
			'data-test-id': 'bet-report-registry-account-link',
			to: accountRoute,
			component: Link,
		},
	});

	const CashdeskLink = reflect({
		view: Anchor<typeof Link<StoreValue<typeof cashdeskRoute.$params>>>,
		bind: {
			'data-test-id': 'bet-report-registry-cashdesk-link',
			to: cashdeskRoute,
			component: Link,
		},
	});

	const GameLink = reflect({
		view: Anchor<typeof Link<StoreValue<typeof gameSettingsRoute.$params>>>,
		bind: {
			'data-test-id': 'bet-report-registry-game-link',
			to: gameSettingsRoute,
			component: Link,
		},
	});

	const Registry = createRegistry(registryModel);

	const BetReportRegistryTotal = forwardRef<HTMLTableSectionElement, RegistryTableTfootProps>(
		(props, ref) => {
			const { t } = useTranslation();
			const [total, isMultiCurrencies] = useUnit([model.$total, model.$isMultiCurrencies]);

			if (!total) return null;

			return (
				<Registry.Table.Tfoot
					ref={ref}
					{...props}
				>
					<Registry.Table.Tr>
						<Registry.Table.Td colSpan={4}>{t('bet-report-registry.total')}</Registry.Table.Td>
						<Registry.Table.Td colSpan={2}>
							{!isMultiCurrencies && total.currency}
						</Registry.Table.Td>
						<Registry.Table.Td>
							{!isMultiCurrencies && <NumberFormatter value={total.wager} />}
						</Registry.Table.Td>
						<Registry.Table.Td colSpan={2}>
							{!isMultiCurrencies && <NumberFormatter value={total.winAmount} />}
						</Registry.Table.Td>
					</Registry.Table.Tr>
				</Registry.Table.Tfoot>
			);
		},
	);

	const BetReportRegistry = forwardRef<HTMLDivElement, BetReportRegistryProps>(
		({ topBarActionSlot, emptyActionSlot, ...props }, ref) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					<Registry.Title>{t('bet-report-registry.title')}</Registry.Title>
					<Registry.TopBar
						alwaysShow
						className={cls.betReportRegistryTopBar}
					>
						<Registry.Filters
							visibleFrom='md'
							classNames={{
								root: cls.betReportRegistryFilters,
								inner: cls.betReportRegistryFiltersInner,
							}}
						>
							<div className={cls.betReportRegistryFiltersGroup}>
								<DateField />
								<TimeZoneField />
							</div>
							<div className={cls.betReportRegistryFiltersGroup}>
								<GamesField />
								<CashdesksField />
								<CurrenciesField />
							</div>
						</Registry.Filters>
						<div className={cls.betReportRegistryActions}>{topBarActionSlot}</div>
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							<Registry.Table.Col className={cls.betReportRegistryColDate} />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								<Registry.Table.Th>{t('bet-report-registry.table-columns.date')}</Registry.Table.Th>
								<Registry.Table.Th>
									{t('bet-report-registry.table-columns.account')}
								</Registry.Table.Th>
								<Registry.Table.Th>{t('bet-report-registry.table-columns.game')}</Registry.Table.Th>
								<Registry.Table.Th>
									{t('bet-report-registry.table-columns.cashdesk')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('bet-report-registry.table-columns.currency')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('bet-report-registry.table-columns.status')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('bet-report-registry.table-columns.wager')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('bet-report-registry.table-columns.win-amount')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('bet-report-registry.table-columns.multiplier')}
								</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList>
								{(row) => (
									<>
										<Registry.Table.Td>{row.date}</Registry.Table.Td>
										<Registry.Table.Td>
											<AccountLink query={{ id: row.account.id }}>{row.account.name}</AccountLink>
										</Registry.Table.Td>
										<Registry.Table.Td>
											<GameLink params={{ id: row.game.id }}>{row.game.name}</GameLink>
										</Registry.Table.Td>
										<Registry.Table.Td>
											<CashdeskLink params={{ id: row.cashdesk.id }}>
												{row.cashdesk.name}
											</CashdeskLink>
										</Registry.Table.Td>
										<Registry.Table.Td>{row.currency}</Registry.Table.Td>
										<Registry.Table.Td>
											{t(`bet-report-registry.status.${row.status}`)}
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.wager} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.winAmount} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.multiplier} />
										</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
						<BetReportRegistryTotal />
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('bet-report-registry.empty.title')}
						message={t('bet-report-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						withClearFiltersButton={false}
						icon={<FileSearch />}
						title={t('bet-report-registry.table-empty.title')}
						message={t('bet-report-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return BetReportRegistry;
};
